import { HeaderAndContainer } from '@trucktrax/trucktrax-common';
import { PermissionAccess, RegionDto, UserDto } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import ReactTable, { RowInfo } from 'react-table';

import { NO_RESULTS_IMAGE } from '../../../constants/appConstants';
import { EMAIL_NOTIFICATIONS_TEXT, USERS_TEXT } from '../../../constants/navConstants';
import {
  getAllSubscriptions,
  getSubscriptionPlants,
  PlantOption,
  plantOptionNames,
  setSubscriptionActive,
} from '../../../services/emailSubscriptionsService';
import { fetchUsers } from '../../../services/usersService';
import {
  EmailNotificationSubscriptionDto,
  EmailNotificationType,
  EmailSubscriptionGroup,
} from '../../../types';
import { addDataTest, addHeaderDataTest, getRegionUrlToNameMap } from '../../../util/adminUtil';
import {
  groupSubscriptions,
  groupTypeLabels,
  NOTIFICATION_TYPE_OPTIONS,
  GROUP_STATUS_LABELS,
  groupStatus,
  GroupStatus,
  makePlantLabel,
  normalizeEmail,
  subscriptionTypeLabel,
} from '../../../util/emailNotificationUtil';
import { devErrorAndLog } from '../../../util/errorUtil';
import { userDisplayName } from '../../../util/userUtil';
import tableStyles from '../../shared/DataTableContent.module.css';
import EmptyDataTableContent from '../../shared/EmptyDataTableContent';
import Autocomplete from '../../shared/forms/Autocomplete';
import GroupedMultiSelect from '../../shared/forms/GroupedMultiSelect';
import Header from '../../shared/Header';
import searchStyles from '../../shared/header/VehicleSearch.module.css';
import GeoTraxPagination from '../../shared/pagination/Pagination';
import EmailNotificationsAddModal from './EmailNotificationsAddModal';
import SubscriptionRowsTable from './SubscriptionRowsTable';
import styles from './emailNotifications.module.css';
import { activeAndDeactivatedRegionsSelector } from '../../../store/selectors/regionSelectors';

/**
 * Kept at module scope so the column's Cell returns a call result, the shape every
 * other admin column uses. An inline arrow returning JSX reads to eslint as a
 * component defined during render.
 */
const renderStatusCell = (status: GroupStatus) => (
  <span
    className={status === 'active' ? undefined : styles.statusInactive}
    data-test="admin-email-notification-status"
  >
    {GROUP_STATUS_LABELS[status]}
  </span>
);

/**
 * react-table builds every rowInfo with the row it came from on `original`
 * (react-table/lib/index.js), but @types/react-table's RowInfo omits the field --
 * it documents `row` as "Original row from your data", which is the post-accessed
 * row instead. Named here rather than casting to any at each use.
 */
interface GroupRowInfo extends RowInfo {
  original?: EmailSubscriptionGroup;
}

/**
 * One header filter: its label, then its control.
 *
 * The label sits beside the control rather than above it, which is what Vehicles,
 * Devices and Drivers do with their header filters. GroupedMultiSelect can render its
 * own stacked label -- the modal uses that -- so none is passed here.
 */
const renderFilter = (label: string, control: ReactNode) => (
  <div className={styles.filterGroup}>
    <span className={styles.filterLabel}>{label}:</span>
    {control}
  </div>
);

/**
 * The User cell: the account that owns the address, linked to its profile.
 *
 * Subscriptions are keyed by address, not by user url, so the association is by
 * address and an address belonging to nobody -- a shared inbox, a distribution alias --
 * is left blank rather than guessed at.
 *
 * Linked only with access to the Users screen, since that is where it lands; without
 * it the name still shows, as Members does for a member with no permission to view.
 * The link stops propagation, or the row click would open the editor behind it.
 */
/**
 * How a recipient is named: the account's name, or the bare address when the address
 * belongs to no user. Also what the column sorts on, so the two cannot disagree.
 */
export const recipientName = (user: UserDto | undefined, group: EmailSubscriptionGroup) => {
  const name = userDisplayName(user);
  return name !== '' ? name : group.email;
};

/**
 * The recipient cell: the account that receives, with an icon to its profile.
 *
 * The icon carries the link rather than the name, so the name reads as identifying
 * the recipient and the icon reads as the separate act of navigating to them --
 * hovering the name alone no longer looks like a click away from this row.
 *
 * Linked only with access to the Users screen, since that is where it lands; without
 * it the name still shows, as Members does for a member with no permission to view.
 * The link stops propagation, or the row click would open the editor behind it.
 *
 * An address belonging to nobody -- a shared inbox, a distribution alias -- shows the
 * address and links nowhere, since there is no profile to open.
 */
const renderRecipientCell = (
  user: UserDto | undefined,
  group: EmailSubscriptionGroup,
  canViewUser: boolean,
) => {
  const name = recipientName(user, group);
  if (!user || userDisplayName(user) === '' || !canViewUser) {
    return addDataTest('txt-bold', 'admin-email-notification-user', name);
  }
  return (
    <span data-test="admin-email-notification-user">
      <span className="txt-bold">{name}</span>
      <Link
        to={{ pathname: `/admin/users/${user.id}` }}
        className={styles.userProfileLink}
        onClick={event => event.stopPropagation()}
        aria-label={`Open ${name}’s profile`}
        data-test="admin-email-notification-user-link"
      >
        <i aria-hidden="true" className="icon-new-tab" />
      </Link>
    </span>
  );
};

export const SEARCH_HINT = 'Search by user or email';

/**
 * The Email Notifications admin screen: one row per address, with the notifications
 * and scope it receives.
 *
 * Unlike every other admin table this one does not go through DataTableContent, and
 * that is forced rather than preferred. DataTableContent pages, sorts and searches
 * on the server; this screen shows an address, which is the grouping of several
 * stored rows, and one address's rows can straddle a page boundary -- a page of rows
 * cannot be grouped into correct addresses. The API cannot group either, and its
 * filters take a single NotificationType and a single PlantUrl, which the
 * multi-select filters here would need several of.
 *
 * So the screen reads the whole collection once (`Size: 0`, as
 * NotificationDispatchService does) and owns its filtering, sorting and paging. It
 * is admin configuration, not operational data: the row count is bounded by how many
 * addresses an organisation subscribes, not by traffic. It reuses DataTableContent's
 * own chrome -- the header/container frame, the table classes, the pagination and the
 * empty state -- so it reads as the same screen despite the different plumbing.
 *
 * Deliberately not filtered by the admin region selector: an address is configured
 * against plants across every region the admin can reach, so a region-filtered read
 * would split one address's configuration into several half-populated rows.
 *
 * Rows expand. The grouping is what an admin configures, but the stored rows are what
 * actually send, and an address covering a whole region's plant list has a scope no
 * cell can print -- so the row carries counts and the expansion carries the rows
 * themselves. `expanded` is controlled rather than left to react-table, which keys it
 * by row index within the page: without that, sorting or paging leaves the chevron
 * open over a different address than the one whose rows are showing.
 */
export class EmailNotificationsView extends Component<
  EmailNotificationsViewProps,
  EmailNotificationsViewState
> {
  static defaultProps: Partial<EmailNotificationsViewProps> = {
    emailNotificationsPermissionAccess: '',
    regions: [],
    users: [],
    getAllSubscriptions,
    getSubscriptionPlants,
    setSubscriptionActive,
  };

  state: EmailNotificationsViewState = {
    subscriptions: [],
    plantOptions: [],
    isLoading: true,
    searchText: '',
    typeFilter: [],
    plantFilter: [],
    isOpen: false,
    isCloseRequest: false,
    expanded: {},
  };

  async componentDidMount() {
    // Dispatched, not awaited: it lands in redux and re-renders the User column when it
    // arrives, and the table is useful before it does.
    this.props.fetchUsers?.('');
    await this.load();
  }

  /**
   * Plant options are read alongside the subscriptions rather than when the modal
   * opens, because the table needs them too: a row names its plant, and the rows
   * carry plant urls.
   */
  load = async () => {
    this.setState({ isLoading: true, expanded: {} });
    const [subscriptions, plantOptions] = await Promise.all([
      this.props.getAllSubscriptions!(),
      this.props.getSubscriptionPlants!(),
    ]);
    this.setState({ subscriptions, plantOptions, isLoading: false });
  };

  canEdit = () => this.props.emailNotificationsPermissionAccess === PermissionAccess.Edit;

  regionNames = () => getRegionUrlToNameMap(this.props.regions ?? []);

  plantNames = () => plantOptionNames(this.state.plantOptions);

  /**
   * Addresses to the accounts that own them.
   *
   * Keyed on the normalized address because that is the only link between the two --
   * a subscription stores an address, never a user url. A consequence worth knowing:
   * changing a user's email orphans their subscriptions from this column, the same way
   * it orphans them from the profile card.
   */
  usersByEmail = () =>
    // Mutated, not spread: `{ ...all }` per user allocated a fresh map for every account
    // in the tenant, and this is called from both columns() and filteredGroups() -- so
    // once per render and again per filter pass. The map is built here and never shared.
    (this.props.users ?? []).reduce<{ [email: string]: UserDto }>((all, user) => {
      const key = normalizeEmail(user.email);
      if (key !== '' && !all[key]) {
        all[key] = user;
      }
      return all;
    }, {});

  canViewUsers = () => !!this.props.usersPermissionAccess;

  groups = () => groupSubscriptions(this.state.subscriptions);

  /**
   * The notifications an address already has, by address.
   *
   * Handed to the add modal so typing an address that is already in the table prefills
   * it rather than POSTing duplicates against the unique index. Matched on the
   * normalized address, which is what the grouping keys on.
   */
  findGroupForEmail = (email: string) => {
    const key = normalizeEmail(email);
    return key === '' ? undefined : this.groups().find(group => group.key === key);
  };

  /**
   * Filters are OR within a control and AND across them, which is how the checkbox
   * filters on Vehicles behave. An empty control does not narrow anything, rather
   * than matching nothing.
   *
   * A group matches a topic or plant filter when *any* of its rows does: the row is
   * an address, and an address subscribed to Diversions among other things is a
   * Diversions recipient.
   */
  filteredGroups = (): EmailSubscriptionGroup[] => {
    const { searchText, typeFilter, plantFilter } = this.state;
    const term = normalizeEmail(searchText);
    const usersByEmail = this.usersByEmail();

    return this.groups().filter(group => {
      // Matches the name as well as the address, because the name is what the column
      // shows: a table you can read a user's name off but not search for it by is a
      // table whose search looks broken.
      if (
        term !== '' &&
        !group.key.includes(term) &&
        !normalizeEmail(recipientName(usersByEmail[group.key], group)).includes(term)
      ) {
        return false;
      }
      if (
        typeFilter.length > 0 &&
        !group.notificationTypes.some(type => typeFilter.includes(type))
      ) {
        return false;
      }
      if (
        plantFilter.length > 0 &&
        !group.plants.some(plant => plantFilter.includes(plant.url ?? ''))
      ) {
        return false;
      }
      return true;
    });
  };

  openModal = () => this.setState({ isOpen: true, isCloseRequest: false, editing: undefined });

  /**
   * A row click opens the same editor over the address, prefilled. The status cell
   * stops propagation, so toggling does not also land here.
   */
  onRowClick = (group?: EmailSubscriptionGroup) => () => {
    if (!group || !this.canEdit()) {
      return;
    }
    this.setState({ isOpen: true, isCloseRequest: false, editing: group });
  };

  /** Keeps the modal mounted so AdminAddModal's discard prompt can render. */
  requestCloseModal = () => this.setState({ isOpen: true, isCloseRequest: true });

  closeModal = () => this.setState({ isOpen: false, isCloseRequest: false });

  onSaved = async () => {
    this.closeModal();
    // Re-read rather than patching in place: a save writes several rows and the
    // ids of the new ones are only known from the server.
    await this.load();
  };

  /**
   * Turn one subscription on or off, from the expanded detail.
   *
   * Updated in place rather than by re-reading, because a re-read clears the expansion
   * and would close the panel the admin is working in. Safe to keep the expansion:
   * `expanded` is keyed by row index, and flipping a row's `active` does not reorder
   * or drop an address from the filtered list -- none of the filters key on status.
   *
   * On failure the server state is unknown, so it re-reads instead of guessing --
   * which does collapse the panel, but only on the error path.
   */
  onToggleSubscription = async (
    subscriptions: EmailNotificationSubscriptionDto[],
    active: boolean,
  ) => {
    const writable = subscriptions.filter(subscription => subscription.id != null);
    if (writable.length === 0) {
      return;
    }

    // Settled individually: a region toggle is several records, and failing the whole
    // gesture because one was rejected would leave the screen disagreeing with the
    // server about the rest.
    const results = await Promise.allSettled(
      writable.map(subscription => this.props.setSubscriptionActive!(subscription, active)),
    );

    const failed = results.filter(result => result.status === 'rejected');
    if (failed.length > 0) {
      this.props.devErrorAndLog!(
        `Unable to ${active ? 'activate' : 'deactivate'} ${
          failed.length === 1
            ? `the ${subscriptionTypeLabel(writable[0])} notification`
            : `${failed.length} notifications`
        } for \u201c${writable[0].email}\u201d.`,
        `Email Notifications: ${(failed[0] as PromiseRejectedResult).reason?.toString()}`,
      );
      // Server state is now unknown for at least one row, so re-read rather than guess
      // -- which collapses the panel, but only on the error path.
      await this.load();
      return;
    }

    const changed = new Set(writable.map(subscription => subscription.id));
    this.setState(previous => ({
      subscriptions: previous.subscriptions.map(existing =>
        changed.has(existing.id) ? { ...existing, active } : existing,
      ),
    }));
  };

  /** Plant filter options, grouped by region exactly as the modal groups them. */
  plantFilterOptions = () => {
    const regionNames = this.regionNames();
    return [...this.state.plantOptions]
      .sort((a, b) => {
        const regionOrder = (regionNames[a.region.url ?? ''] ?? '').localeCompare(
          regionNames[b.region.url ?? ''] ?? '',
        );
        return regionOrder !== 0 ? regionOrder : a.name.localeCompare(b.name);
      })
      .map(option => ({
        key: option.key,
        label: option.name,
        group: regionNames[option.region.url ?? ''] ?? '',
      }));
  };

  /** Names a row's plant. See `makePlantLabel` for why the map is required. */
  plantLabel = () => makePlantLabel(this.plantNames());

  static regionsAccessor =
    (regionNames: { [url: string]: string }) => (group: EmailSubscriptionGroup) =>
      group.regions
        .map(region => regionNames[region.url ?? ''] ?? '')
        .filter(name => name !== '')
        .sort()
        .join(', ');

  /** The Plants cell: the address's plants, named through the option list and sorted -- the same shape the Regions column already reads its cell off. */
  static plantsAccessor =
    (plantNames: { [url: string]: string }) => (group: EmailSubscriptionGroup) =>
      group.plants
        .map(plant => plantNames[plant.url ?? ''] ?? '')
        .filter(name => name !== '')
        .sort()
        .join(', ');

  columns = () => {
    const regionNames = this.regionNames();
    const plantNames = this.plantNames();
    const usersByEmail = this.usersByEmail();
    const canViewUser = this.canViewUsers();

    return [
      {
        /*
         * react-table only renders an expander where a column declares one; the
         * chevron itself is the default ExpanderComponent, which .geotraxTable already
         * styles (DataTableContent.module.scss) -- it hides the bullet and draws the
         * triangle, so no custom Expander is needed.
         */
        expander: true,
        id: 'expander',
        Header: '',
        width: 44,
        sortable: false,
      },
      {
        /*
         * The recipient, named as a person rather than as an address. Two columns used
         * to say this -- Email and User -- which is one thing twice.
         *
         * Falls back to the address, which is not a rare edge: an address belonging to
         * no user is supported (a shared inbox, a distribution alias), and it still has
         * to be identifiable and searchable.
         */
        Header: addHeaderDataTest(null, 'sort-email-notification-by-user', 'User'),
        id: 'user',
        accessor: (group: EmailSubscriptionGroup) => recipientName(usersByEmail[group.key], group),
        minWidth: 260,
        Cell: (props: { original: EmailSubscriptionGroup }) =>
          renderRecipientCell(usersByEmail[props.original.key], props.original, canViewUser),
      },
      {
        Header: addHeaderDataTest(null, 'sort-email-notification-by-types', 'Notifications'),
        id: 'notificationTypes',
        accessor: (group: EmailSubscriptionGroup) => groupTypeLabels(group).join(', '),
        minWidth: 200,
        Cell: (props: { value: string }) =>
          addDataTest(null, 'admin-email-notification-types', props.value),
      },
      {
        Header: addHeaderDataTest(null, 'sort-email-notification-by-regions', 'Regions'),
        id: 'regions',
        accessor: EmailNotificationsView.regionsAccessor(regionNames),
        minWidth: 160,
        Cell: (props: { value: string }) =>
          addDataTest(null, 'admin-email-notification-regions', props.value),
      },
      {
        Header: addHeaderDataTest(null, 'sort-email-notification-by-plants', 'Plants'),
        id: 'plants',
        accessor: EmailNotificationsView.plantsAccessor(plantNames),
        minWidth: 170,
        Cell: (props: { value: string }) =>
          addDataTest(null, 'admin-email-notification-plants', props.value),
      },
      {
        Header: addHeaderDataTest(null, 'sort-email-notification-by-status', 'Active'),
        id: 'active',
        // The rollup, which is also what the column sorts on. The switches live on the
        // rows inside the expansion, where the status they set actually belongs.
        accessor: groupStatus,
        minWidth: 130,
        Cell: (props: { value: GroupStatus }) => renderStatusCell(props.value),
      },
    ];
  };

  /**
   * Every narrowing goes through here so the expansion collapses with it. `expanded`
   * is keyed by row index, so a filter that reorders or removes rows would otherwise
   * leave the chevron open over whichever address inherited that index.
   */
  narrow = (changes: Partial<EmailNotificationsViewState>) => {
    this.setState({ ...changes, expanded: {} } as EmailNotificationsViewState);
  };

  renderFilters = () => (
    <div className={styles.filterBar}>
      {renderFilter(
        'Notifications',
        <GroupedMultiSelect
          className={styles.filterControl}
          items={NOTIFICATION_TYPE_OPTIONS}
          value={this.state.typeFilter}
          onChange={typeFilter =>
            this.narrow({ typeFilter: typeFilter as EmailNotificationType[] })
          }
          placeholder="All notifications"
          dataTest="email-notification-type-filter"
        />,
      )}
      {renderFilter(
        'Plants',
        <GroupedMultiSelect
          className={styles.filterControl}
          items={this.plantFilterOptions()}
          value={this.state.plantFilter}
          onChange={plantFilter => this.narrow({ plantFilter })}
          placeholder="All plants"
          showSelectAll
          dataTest="email-notification-plant-filter"
        />,
      )}
    </div>
  );

  /**
   * The expanded detail: the rows actually stored for the address, which is what the
   * dispatcher reads. Shaped like the user detail view's subscriptions card, the other
   * place these rows are listed, so the two read alike.
   *
   * Status is settable here, per row -- that is where it lives in the API and it is
   * the only thing about a row that is not part of its identity. Everything else goes
   * through the modal, which diffs the whole address at once; deleting one row here
   * would leave a set the next save would silently restore.
   */
  renderSubRows = (rowInfo: RowInfo) => {
    const group = (rowInfo as GroupRowInfo)?.original;
    if (!group) {
      return null;
    }

    return (
      <div className={styles.subRows} data-test="admin-email-notification-detail">
        {/*
          Arranged exactly as the user profile's card is -- topic on a tab, then a row per
          region carrying its plants and one switch. The two places list the same thing,
          so they read the same way, and a region's plants are turned on and off together
          in both.

          That means no per-subscription switch here either. This panel is a read-out of
          what an address receives; changing which plants it covers goes through the
          modal, which is also the only place a subscription is removed.
        */}
        <SubscriptionRowsTable
          subscriptions={group.subscriptions}
          regionNames={this.regionNames()}
          plantLabel={this.plantLabel()}
          canEdit={this.canEdit()}
          onToggle={this.onToggleSubscription}
          groupByTopic
          dataTest="admin-email-notification-detail"
        />
      </div>
    );
  };

  renderEmpty = () => (
    <EmptyDataTableContent
      img={{ src: NO_RESULTS_IMAGE, alt: 'No email notifications' }}
      component={
        <div className={tableStyles.noItemContentCt}>
          <h3 className={cx('txt-bold', 'margin-bottom-0')}>No email notifications found.</h3>
          {this.canEdit() ? (
            <p>
              Try modifying your search query, or create a new one by clicking the
              <span>
                <i aria-hidden="true" className={cx('icon-add-circle', tableStyles.icon)} />
              </span>
              button below.
            </p>
          ) : (
            <p>Try modifying your search query or adjusting your filters.</p>
          )}
        </div>
      }
    />
  );

  render() {
    const groups = this.filteredGroups();
    const hasContent = groups.length > 0;

    const table = (
      <div
        className={cx(
          hasContent ? tableStyles.geotraxTable : tableStyles.geotraxTableNoData,
          styles.notificationsTable,
        )}
      >
        <div className={tableStyles.tableContainer}>
          <ReactTable
            PaginationComponent={GeoTraxPagination}
            data={groups}
            columns={hasContent ? this.columns() : []}
            /*
              The recipient column. It was 'email' until Email and User were merged into
              one column -- react-table silently ignores a defaultSorted naming a column
              that does not exist, so the table opened unsorted.
            */
            defaultSorted={[{ id: 'user' }]}
            loading={this.state.isLoading}
            minRows={0}
            noDataText="No results found."
            NoDataComponent={this.renderEmpty}
            SubComponent={this.renderSubRows}
            expanded={this.state.expanded}
            onExpandedChange={(expanded: { [index: number]: boolean }) =>
              this.setState({ expanded })
            }
            // Both reorder the page, and `expanded` is keyed by index within it.
            onSortedChange={() => this.setState({ expanded: {} })}
            onPageChange={() => this.setState({ expanded: {} })}
            getTrProps={(_: unknown, rowInfo?: GroupRowInfo) => ({
              onClick: this.onRowClick(rowInfo?.original),
              'data-test': 'admin-list-row',
            })}
            getTdProps={(_: unknown, __: unknown, column: any) => ({
              'data-test': column?.expander ? 'admin-list-expander' : 'admin-list-cell',
              /*
               * The row opens the editor, so expanding must not. react-table hands a
               * getTdProps onClick the original handler rather than replacing it, so
               * the toggle only happens if handleOriginal is called.
               */
              onClick: column?.expander
                ? (event: React.SyntheticEvent, handleOriginal?: () => void) => {
                    event.stopPropagation();
                    if (handleOriginal) {
                      handleOriginal();
                    }
                  }
                : undefined,
            })}
            getTheadThProps={() => ({ 'data-test': 'admin-list-header' })}
            getTbodyProps={() => ({ 'data-test': 'admin-list-body' })}
            style={{ maxHeight: '100%' }}
            showPagination={hasContent}
          />
        </div>
      </div>
    );

    return (
      <div>
        <HeaderAndContainer
          elementsLeft={<Header title={EMAIL_NOTIFICATIONS_TEXT} />}
          elementsRight={[
            <div key="filters" className={tableStyles.headerRightControls}>
              {this.renderFilters()}
            </div>,
            <div key="search" className={searchStyles.searchWrapper}>
              <Autocomplete
                dataTest="email-notification-search"
                hintText={SEARCH_HINT}
                onUpdateInput={(searchText: string) => this.narrow({ searchText })}
                searchText={this.state.searchText}
                dataSource={[]}
                openOnFocus={false}
                autocompleteHeight="500px"
                clearSearch={() => this.narrow({ searchText: '' })}
                onNewRequest={() => {}}
              />
            </div>,
          ]}
          containerComponent={table}
          containerStyle={tableStyles.headerAndContainer}
        />
        {this.canEdit() && (
          <EmailNotificationsAddModal
            isOpen={this.state.isOpen}
            group={this.state.editing}
            findGroupForEmail={this.findGroupForEmail}
            // The account behind the edited address, so the locked recipient reads as a
            // name. The table already resolves this for its User column.
            recipientUser={
              this.state.editing ? this.usersByEmail()[this.state.editing.key] : undefined
            }
            plantOptions={this.state.plantOptions}
            regionNames={this.regionNames()}
            openModal={this.openModal}
            closeModal={this.requestCloseModal}
            discardAcceptAction={this.closeModal}
            discardRejectAction={this.openModal}
            isCloseRequest={this.state.isCloseRequest}
            onSaved={this.onSaved}
          />
        )}
      </div>
    );
  }
}

export interface EmailNotificationsViewState {
  subscriptions: EmailNotificationSubscriptionDto[];
  plantOptions: PlantOption[];
  isLoading: boolean;
  searchText: string;
  typeFilter: EmailNotificationType[];
  /** Plant urls. */
  plantFilter: string[];
  isOpen: boolean;
  isCloseRequest: boolean;
  /** The address the editor is open on. Absent when creating. */
  editing?: EmailSubscriptionGroup;
  /** Which rows are expanded, keyed by index within the current page. */
  expanded: { [index: number]: boolean };
}

export interface EmailNotificationsViewProps {
  emailNotificationsPermissionAccess?: string;
  /** Gates the User column's link, since it lands on the Users screen. */
  usersPermissionAccess?: string;
  regions?: RegionDto[];
  /** Every user the caller can see, for associating an address with an account. */
  users?: UserDto[];
  fetchUsers?: typeof fetchUsers;
  devErrorAndLog?: typeof devErrorAndLog;
  getAllSubscriptions?: typeof getAllSubscriptions;
  getSubscriptionPlants?: typeof getSubscriptionPlants;
  setSubscriptionActive?: typeof setSubscriptionActive;
}

const mapStateToProps = (state: any) => ({
  emailNotificationsPermissionAccess: state.adminPermissionAccess[EMAIL_NOTIFICATIONS_TEXT],
  usersPermissionAccess: state.adminPermissionAccess[USERS_TEXT],
  regions: activeAndDeactivatedRegionsSelector(state),
  users: state.usersList?.users,
});

export default connect<any, any, any>(mapStateToProps, { devErrorAndLog, fetchUsers })(
  EmailNotificationsView,
);
